// src/components/surveys/builder/ConditionalLogicEditor.tsx
import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  GitBranch,
  ChevronDown,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import {
  SurveyQuestion,
  ConditionalLogicRule,
  ConditionOperator,
} from '../../../types/survey.types';

interface ConditionalLogicEditorProps {
  questions: SurveyQuestion[];
  rules: ConditionalLogicRule[];
  onAddRule: (rule: Omit<ConditionalLogicRule, 'id'>) => void;
  onUpdateRule: (ruleId: string, updates: Partial<ConditionalLogicRule>) => void;
  onDeleteRule: (ruleId: string) => void;
}

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: 'is greater than',
  less_than: 'is less than',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

const ACTION_LABELS = {
  show: 'Show question',
  hide: 'Hide question',
  require: 'Make required',
  skip_to: 'Skip to question',
};

const ConditionalLogicEditor: React.FC<ConditionalLogicEditorProps> = ({
  questions,
  rules,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newRule, setNewRule] = useState<{
    sourceQuestionId: string;
    operator: ConditionOperator;
    conditionValue: string;
    action: 'show' | 'hide' | 'require' | 'skip_to';
    targetQuestionId: string;
  }>({
    sourceQuestionId: '',
    operator: 'equals',
    conditionValue: '',
    action: 'show',
    targetQuestionId: '',
  });

  const handleAddRule = () => {
    if (!newRule.sourceQuestionId || !newRule.targetQuestionId) {
      return;
    }

    onAddRule({
      sourceQuestionId: newRule.sourceQuestionId,
      operator: newRule.operator,
      conditionValue: newRule.conditionValue,
      action: newRule.action,
      targetQuestionId: newRule.targetQuestionId,
    });

    setNewRule({
      sourceQuestionId: '',
      operator: 'equals',
      conditionValue: '',
      action: 'show',
      targetQuestionId: '',
    });
    setIsAdding(false);
  };

  const getQuestionById = (id: string) => questions.find((q) => q.id === id);

  const getConditionValueOptions = (questionId: string) => {
    const question = getQuestionById(questionId);
    if (!question) return null;

    if (question.type === 'yes_no') {
      return [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ];
    }

    if (['multiple_choice', 'multi_select', 'dropdown'].includes(question.type)) {
      return question.options?.map((opt) => ({
        value: opt.id,
        label: opt.text,
      }));
    }

    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-indigo-600" />
          <h3 className="font-medium text-gray-900">Conditional Logic</h3>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>

      {/* Existing rules */}
      {rules.length === 0 && !isAdding && (
        <p className="text-sm text-gray-500 text-center py-4">
          No conditional logic rules. Add rules to show, hide, or skip questions based on answers.
        </p>
      )}

      <div className="space-y-3">
        {rules.map((rule) => {
          const sourceQuestion = getQuestionById(rule.sourceQuestionId);
          const targetQuestion = getQuestionById(rule.targetQuestionId);
          const conditionOptions = getConditionValueOptions(rule.sourceQuestionId);
          const conditionLabel = conditionOptions?.find(
            (opt) => opt.value === rule.conditionValue
          )?.label;

          return (
            <div
              key={rule.id}
              className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">If</span>{' '}
                    <span className="text-indigo-600">
                      Q{(sourceQuestion?.order || 0) + 1}: {sourceQuestion?.text || 'Unknown'}
                    </span>{' '}
                    <span className="font-medium">{OPERATOR_LABELS[rule.operator]}</span>{' '}
                    <span className="text-orange-600">
                      "{conditionLabel || rule.conditionValue}"
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                    <ArrowRight className="h-4 w-4" />
                    <span className="font-medium">{ACTION_LABELS[rule.action]}</span>{' '}
                    <span className="text-indigo-600">
                      Q{(targetQuestion?.order || 0) + 1}: {targetQuestion?.text || 'Unknown'}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => onDeleteRule(rule.id)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add new rule form */}
      {isAdding && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-4">
          <h4 className="font-medium text-gray-900">New Conditional Rule</h4>

          {/* If condition */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">IF</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Source question */}
              <select
                value={newRule.sourceQuestionId}
                onChange={(e) =>
                  setNewRule({
                    ...newRule,
                    sourceQuestionId: e.target.value,
                    conditionValue: '',
                  })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select question...</option>
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    Q{q.order + 1}: {q.text.substring(0, 30)}
                    {q.text.length > 30 ? '...' : ''}
                  </option>
                ))}
              </select>

              {/* Operator */}
              <select
                value={newRule.operator}
                onChange={(e) =>
                  setNewRule({
                    ...newRule,
                    operator: e.target.value as ConditionOperator,
                  })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                {Object.entries(OPERATOR_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Condition value */}
              {!['is_empty', 'is_not_empty'].includes(newRule.operator) && (
                <>
                  {getConditionValueOptions(newRule.sourceQuestionId) ? (
                    <select
                      value={newRule.conditionValue}
                      onChange={(e) =>
                        setNewRule({ ...newRule, conditionValue: e.target.value })
                      }
                      className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select value...</option>
                      {getConditionValueOptions(newRule.sourceQuestionId)?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={newRule.conditionValue}
                      onChange={(e) =>
                        setNewRule({ ...newRule, conditionValue: e.target.value })
                      }
                      placeholder="Enter value..."
                      className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Then action */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">THEN</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Action */}
              <select
                value={newRule.action}
                onChange={(e) =>
                  setNewRule({
                    ...newRule,
                    action: e.target.value as 'show' | 'hide' | 'require' | 'skip_to',
                  })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                {Object.entries(ACTION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Target question */}
              <select
                value={newRule.targetQuestionId}
                onChange={(e) =>
                  setNewRule({ ...newRule, targetQuestionId: e.target.value })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select question...</option>
                {questions
                  .filter((q) => q.id !== newRule.sourceQuestionId)
                  .map((q) => (
                    <option key={q.id} value={q.id}>
                      Q{q.order + 1}: {q.text.substring(0, 30)}
                      {q.text.length > 30 ? '...' : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleAddRule}
              disabled={!newRule.sourceQuestionId || !newRule.targetQuestionId}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Rule
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionalLogicEditor;
